<script>
  import Icon from "@iconify/svelte";
  import { t } from "../lib/i18n";

  import settingsIcon from "@iconify/icons-mdi/cog-outline";
  import clockIcon from "@iconify/icons-mdi/clock-outline";
  import timerIcon from "@iconify/icons-mdi/timer-outline";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";
  import tagIcon from "@iconify/icons-mdi/tag-outline";

  export let apiBase = ""; // NEW (kept for parity / future use)
  export let apiKey = "";  // NEW

  export let setup = null;
  export let setupErr = "";

  function envValue(name) {
    const env = setup?.env || [];
    const hit = env.find((e) => e.name === name);
    return hit?.value || hit?.default || "";
  }

  function item(label, value, hint = "") {
    return { label, value, hint };
  }

  function section(title, items) {
    return { title, items };
  }

  function pretty(v) {
    const s = (v ?? "").toString().trim();
    return s ? s : "—";
  }

  $: meta = setup?.meta || null;

  $: sections = [
    section($t('settings.section_ui'), [
      item($t('settings.ui_enabled'), pretty(envValue("UI_ENABLED"))),
      item($t('settings.ui_dist_dir'), pretty(envValue("UI_DIST_DIR"))),
      item($t('settings.ui_auth_username'), pretty(envValue("MONOCLE_USERNAME"))),
      item($t('settings.ui_auth_password'), envValue("MONOCLE_PASSWORD") ? $t('common.set') : $t('common.not_set'))
    ]),

    section($t('settings.section_api_auth'), [
      item($t('settings.shared_api_key'), envValue("SHARED_API_KEY") ? $t('common.set') : $t('common.not_set')),
      item($t('settings.shared_api_secret'), envValue("SHARED_API_SECRET") ? $t('common.set') : $t('common.not_set'))
    ]),

    section($t('settings.section_core_worker'), [
      item($t('settings.worker_id'), pretty(envValue("WORKER_ID")), $t('settings.defaults_to_hostname')),
      item($t('settings.max_workers'), pretty(envValue("MAX_WORKERS"))),
      item($t('settings.job_timeout'), pretty(envValue("JOB_TIMEOUT"))),
      item($t('settings.http_port'), pretty(envValue("HTTP_PORT"))),
      item($t('settings.max_workers_cap'), pretty(envValue("MAX_WORKERS_CAP")))
    ]),

    section($t('settings.section_tika'), [
      item($t('settings.tika_url'), pretty(envValue("TIKA_URL"))),
      item($t('settings.tika_health_timeout'), pretty(envValue("TIKA_HEALTH_TIMEOUT")))
    ]),

    section($t('settings.section_queue'), [
      item($t('settings.queue_backend'), pretty(envValue("QUEUE_BACKEND"))),
      item($t('settings.redis_address'), pretty(envValue("REDIS_ADDR"))),
      item($t('settings.redis_password'), envValue("REDIS_PASSWORD") ? $t('common.set') : $t('common.not_set')),
      item($t('settings.input_queue'), pretty(envValue("REDIS_INPUT_QUEUE"))),
      item($t('settings.results_queue'), pretty(envValue("REDIS_RESULTS_QUEUE"))),
      item($t('settings.results_ttl'), pretty(envValue("REDIS_RESULTS_TTL")), $t('settings.hours_hint'))
    ]),

    section($t('settings.section_storage'), [
      item($t('settings.storage_backend'), pretty(envValue("STORAGE_BACKEND")), $t('settings.storage_hint')),

      item($t('settings.s3_endpoint'), pretty(envValue("S3_ENDPOINT"))),
      item($t('settings.s3_region'), pretty(envValue("S3_REGION"))),
      item($t('settings.use_ssl'), pretty(envValue("S3_USE_SSL"))),
      item($t('settings.force_path_style'), pretty(envValue("S3_FORCE_PATH_STYLE"))),

      item($t('settings.access_key_id'), envValue("S3_ACCESS_KEY_ID") ? $t('common.set') : $t('common.not_set')),
      item($t('settings.secret_access_key'), envValue("S3_SECRET_ACCESS_KEY") ? $t('common.set') : $t('common.not_set')),

      item($t('settings.input_bucket'), pretty(envValue("S3_INPUT_BUCKET"))),
      item($t('settings.output_bucket'), pretty(envValue("S3_OUTPUT_BUCKET"))),
      item($t('settings.input_prefix'), pretty(envValue("S3_INPUT_PREFIX"))),
      item($t('settings.output_prefix'), pretty(envValue("S3_OUTPUT_PREFIX")))
    ]),

    section($t('settings.section_webhooks'), [
      item($t('settings.default_webhooks'), pretty(envValue("DEFAULT_WEBHOOK_URLS"))),
      item($t('settings.timeout'), pretty(envValue("WEBHOOK_TIMEOUT"))),
      item($t('settings.max_retry'), pretty(envValue("WEBHOOK_MAX_RETRY"))),
      item($t('settings.retry_min_wait'), pretty(envValue("WEBHOOK_RETRY_MIN_WAIT"))),
      item($t('settings.retry_max_wait'), pretty(envValue("WEBHOOK_RETRY_MAX_WAIT"))),
      item($t('settings.secret'), envValue("WEBHOOK_SECRET") ? $t('common.set') : $t('common.not_set'))
    ])
  ];

  // No fetches in this component. We still accept apiKey so App can pass it consistently.
  // If auth is removed mid-session, App will swap to Login anyway.

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";

  const sectionCard =
    "rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5";

  const cell =
    "rounded-xl border border-white/10 bg-white/[0.03] p-3 min-w-0";

  const klabel = "text-xs text-zinc-400 flex flex-wrap items-center gap-2";
  const khint =
    "text-[11px] text-zinc-500 inline-flex items-center gap-1 whitespace-nowrap";

  const headerChip =
    "rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-300 inline-flex items-center gap-2";
</script>

<main class="w-full px-4 py-6">
  <section class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center rounded border border-sky-400/50 bg-sky-300/30 text-sky-50"
        >
          <Icon icon={settingsIcon} width="18" height="18" />
        </span>

        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">
            {$t('settings.title')}
          </h1>
          <p class="mt-0.5 text-sm text-zinc-400">
            {$t('settings.subtitle')}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        {#if meta?.now_utc}
          <div class={headerChip} title={$t('settings.server_time')}>
            <Icon icon={clockIcon} width="16" height="16" />
            <span class="text-zinc-400">{$t('settings.server_time')}</span>
            <span class="font-medium text-zinc-200">{meta.now_utc}</span>
          </div>
        {/if}

        {#if meta?.uptime}
          <div class={headerChip} title={$t('settings.uptime')}>
            <Icon icon={timerIcon} width="16" height="16" />
            <span class="text-zinc-400">{$t('settings.uptime')}</span>
            <span class="font-medium text-zinc-200">{meta.uptime}</span>
          </div>
        {/if}
      </div>
    </div>

    <!-- Error / Loading -->
    {#if setupErr}
      <div
        class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
      >
        <Icon icon={alertIcon} width="18" height="18" />
        <span class="break-words">{setupErr}</span>
      </div>
    {/if}

    {#if !setup}
      <div
        class="mt-6 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400"
      >
        {$t('common.loading')}
      </div>
    {:else}
      <!-- Sections -->
      <div class="mt-6 grid grid-cols-1 gap-4">
        {#each sections as s (s.title)}
          <div class={sectionCard}>
            <div
              class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3"
            >
              <div class="flex items-center gap-2">
                <Icon icon={tagIcon} width="16" height="16" />
                <div class="text-sm font-semibold tracking-wide text-white">
                  {s.title}
                </div>
              </div>

              <div class="text-xs text-zinc-500">{s.items.length} {$t('common.items')}</div>
            </div>

            <div
              class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {#each s.items as it (it.label)}
                <div class={cell}>
                  <div class={klabel}>
                    <span>{it.label}</span>
                    {#if it.hint}
                      <span class={khint}>• {it.hint}</span>
                    {/if}
                  </div>

                  <div class={`${mono} mt-2`} title={it.value}>
                    {it.value}
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </section>
</main>
